import { NrlkException } from "../model/exception.js";
import { NrlkTicket } from "../model/ticket.js";
import { NrlkAdmin } from "../model/admin.js";

const STATUS_MAP = {
  'open': 0,
  'in_progress': 1,
  'closed': 2,
  'approved': 3,
};

function normalizeTicket(t) {
  if (!t) return t;
  return {
    idTicket: t.id,
    titre: t.title,
    description: t.description ?? '',
    dateDebut: t.start_date,
    dateFin: t.end_date,
    dateCloture: t.close_date,
    priorite: t.priority?.name === 'primary' ? 1 : 0,
    idType: t.type?.id,
    idStatut: STATUS_MAP[t.status] ?? 0,
    intervenant: (t.users ?? []).map(u => ({
      idIntervenant: u.id,
      nom: `${u.surname ?? ''} ${u.name ?? ''}`.trim(),
    })),
    intervention: (t.ticket_interventions ?? []).map(i => ({
      idIntervention: i.id,
      description: i.description ?? '',
      date: i.start_date,
      duree: i.duration,
      nom: i.user ? `${i.user.surname ?? ''} ${i.user.name ?? ''}`.trim() : '',
    })),
    fichier: t.files ?? t.fichier ?? [],
    nomClient: t.client?.name ?? '',
  };
}


/**
 * Récupère un ticket par son identifiant.
 *
 * @param {number} idTicket - Identifiant du ticket
 * @returns {Promise<object>} Données du ticket
 * @throws {NrlkException} Si l'ID est absent ou non numérique
 */
export async function getTicket(idTicket) {
    try {
        if (!idTicket || isNaN(idTicket)) {
            throw new NrlkException("L'ID du ticket est obligatoire.");
        }
        const ticket = new NrlkTicket(window.entreprise, window.token);
        const data = await ticket.getTicket(idTicket);
        return normalizeTicket(data.data);
    } catch (error) {
        throw new NrlkException(error);
    }
}

/**
 * Récupère tous les tickets dont le statut est "ouvert".
 *
 * @returns {Promise<Array>} Liste des tickets ouverts
 * @throws {NrlkException}
 */
export async function getTicketsOuverts() {
    try {
        const ticket = new NrlkTicket(String(window.entreprise), String(window.token));
        const data = await ticket.getTicketsOuverts();
        return (data.data ?? []).map(normalizeTicket);
    } catch (error) {
        throw new NrlkException(error);
    }
}

/**
 * Récupère les tickets ouverts d'un client.
 *
 * @param {number} idClient - Identifiant du client
 * @returns {Promise<Array>} Liste des tickets ouverts du client
 * @throws {NrlkException}
 */
export async function getClientTicketsOuverts(idClient) {
    try {
        const ticket = new NrlkTicket(String(window.entreprise), String(window.token));
        const data = await ticket.getClientTicketsOuverts(idClient);
        return (data.data ?? []).map(normalizeTicket).filter(t => t.idStatut <= 1);
    } catch (error) {
        throw new NrlkException(error);
    }
}

/**
 * Récupère les tickets fermés d'un client.
 *
 * @param {number} idClient - Identifiant du client
 * @returns {Promise<Array>} Liste des tickets fermés du client
 * @throws {NrlkException}
 */
export async function getClientTicketsFermes(idClient) {
    try {
        const ticket = new NrlkTicket(String(window.entreprise), String(window.token));
        const data = await ticket.getClientTicketsFermes(idClient);
        return (data.data ?? []).map(normalizeTicket).filter(t => t.idStatut >= 2);
    } catch (error) {
        throw new NrlkException(error);
    }
}

/**
 * Récupère tous les tickets dont le statut est "ferme".
 *
 * @returns {Promise<Array>} Liste des tickets fermés
 * @throws {NrlkException}
 */
export async function getTicketsFermes() {
    try {
        const ticket = new NrlkTicket(window.entreprise, window.token);
        const data = await ticket.getTicketsFermes();
        return (data.data ?? []).map(normalizeTicket);
    } catch (error) {
        throw new NrlkException(error);
    }
}

/**
 * Créer un ticket (API V2)
 *
 * Changements V2 :
 * - idClient → client, idType → type, idStatut → statut (string)
 * - dateDebut → date_debut, dateFin → date_fin (snake_case)
 * - intervenant → utilisateurs (dans l'objet ticket)
 * - modeFacturation/idFacturant supprimés (définis à la clôture)
 * - intervention/mail/actionSpe supprimés du body
 */
let isCreatingTicket = false;
export async function createTicket(idClient, titre, description = "", priorite = 0, dateDebut, dateFin, idStatut, idType, intervenant = [], intervention = [], idFacturant, modeFacturation, custom = {}, envoyerMail = false, mailClient = null) {
    try {
        if (isCreatingTicket) return;
        isCreatingTicket = true;

        if (!idClient || isNaN(idClient)) {
            throw new NrlkException("L'ID du client est obligatoire.");
        }
        if (!titre) {
            throw new NrlkException("Le titre du ticket est obligatoire.");
        }
        if (!dateDebut) {
            throw new NrlkException("La date de début du ticket est obligatoire.");
        }

        const ticket = new NrlkTicket(window.entreprise, window.token);

        const prioriteId = priorite === 1 ? 2 : 1; // 0=normal→id1(secondaire), 1=urgent→id2(primaire)

        const requestBody = {
            "ticket": {
                "client": parseInt(idClient),
                "titre": titre,
                "description": description,
                "priorite": prioriteId,
                "date_debut": dateDebut,
                "date_fin": dateFin ?? null,
                "statut": idStatut ?? "open",
                "type": idType,
                "utilisateurs": (intervenant ?? []).map(u => typeof u === "object" ? u.idIntervenant ?? u.id : u),
                "groupes": [4],
            }
        };

        const data = await ticket.createTicket(requestBody);
        if (!data.result) {
            throw new NrlkException("Erreur lors de la création du ticket : " + data.message);
        }
        return await getTicket(data.data?.id);
    } catch (error) {
        throw new NrlkException(error);
    } finally {
        isCreatingTicket = false;
    }
}

/**
 * Réouvre un ticket préalablement fermé.
 *
 * @param {number} idTicket - Identifiant du ticket
 * @returns {Promise<boolean>} `true` si la réouverture a réussi
 * @throws {NrlkException} Si l'ID est absent ou si l'API retourne une erreur
 */
export async function ouvrirTicket(idTicket) {
    try {
        if (!idTicket || isNaN(idTicket)) {
            throw new NrlkException("L'ID du ticket est obligatoire.");
        }
        const ticket = new NrlkTicket(window.entreprise, window.token);
        const data = await ticket.ouvrirTicket(idTicket);
        if (!data.result) {
            throw new NrlkException("Erreur lors de l'ouverture du ticket : " + data.message);
        }
        return data.result;
    } catch (error) {
        throw new NrlkException(error);
    }
}

/**
 * Clôturer un ticket (API V2)
 *
 * Changements V2 :
 * - Endpoint : /ticket/{id}/fermer (était /ticket/{id}/facturation)
 * - modeFacturation → methode_facturation
 * - idFacturant supprimé du body
 */
export async function facturationTicket(idTicket, modeFacturation, idFacturant, custom = {}) {
    try {
        if (!idTicket || isNaN(idTicket)) {
            throw new NrlkException("L'ID du ticket est obligatoire.");
        }
        if (!modeFacturation) {
            throw new NrlkException("Le mode de facturation est obligatoire.");
        }

        const ticket = new NrlkTicket(window.entreprise, window.token);

        const requestBody = {
            "ticket": {
                "methode_facturation": modeFacturation
            }
        };

        const data = await ticket.facturationTicket(idTicket, requestBody);
        if (!data.result) {
            throw new NrlkException("Erreur lors de la clôture du ticket : " + data.message);
        }
        return data.result;
    } catch (error) {
        throw new NrlkException(error);
    }
}

/**
 * Modifier un ticket (API V2)
 *
 * Changements V2 :
 * - idTicket supprimé du body (dans l'URL)
 * - dateDebut → date_debut, dateFin → date_fin (snake_case)
 * - idType → type
 * - intervenant → utilisateurs (dans l'objet ticket)
 */
export async function updateTicket(idTicket, titre, description, priorite, dateDebut, dateFin, idType, intervenant) {
    try {
        if (!idTicket || isNaN(idTicket)) {
            throw new NrlkException("L'ID du ticket est obligatoire.");
        }
        if (!titre) {
            throw new NrlkException("Le titre du ticket est obligatoire.");
        }
        if (!dateDebut) {
            throw new NrlkException("La date de début du ticket est obligatoire.");
        }

        const ticket = new NrlkTicket(window.entreprise, window.token);

        const prioriteId = priorite === 1 ? 2 : 1;

        const requestBody = {
            "ticket": {
                "titre": titre,
                "description": description,
                "priorite": prioriteId,
                "date_debut": dateDebut,
                "date_fin": dateFin ?? null,
                "type": idType,
                "utilisateurs": (Array.isArray(intervenant) ? intervenant : []).map(u => typeof u === "object" ? u.idIntervenant ?? u.id : u),
            }
        };

        const data = await ticket.updateTicket(idTicket, requestBody);
        if (!data.result) {
            throw new NrlkException("Erreur lors de la modification du ticket : " + data.message);
        }
        return await getTicket(idTicket);
    } catch (error) {
        throw new NrlkException(error);
    }
}

/**
 * Récupère la liste des moyens de facturation disponibles.
 *
 * @returns {Promise<Array>} Liste des moyens de facturation
 * @throws {NrlkException}
 */
export async function getMoyensFacturation() {
    try {
        const admin = new NrlkAdmin(window.entreprise, window.token);
        const data = await admin.getMoyensFacturation();
        return data.data;
    } catch (error) {
        throw new NrlkException(error);
    }
}

/**
 * Récupère la liste des types de ticket disponibles.
 *
 * @returns {Promise<Array>} Liste des types de ticket
 * @throws {NrlkException}
 */
export async function getTypesTicket() {
    try {
        const admin = new NrlkAdmin(window.entreprise, window.token);
        const data = await admin.getTypesTicket();
        return data.data;
    } catch (error) {
        throw new NrlkException(error);
    }
}

/**
 * Ajouter une intervention dans un ticket (API V2)
 *
 * Changements V2 :
 * - Body simplifié : {"intervention": {...}}
 * - idTicket et actionSpe supprimés du body
 */
let isAddingIntervention = false;
export async function addInterventionsTicket(idTicket, intervention) {
    try {
        if (isAddingIntervention) return;
        isAddingIntervention = true;

        if (!idTicket || isNaN(idTicket)) {
            throw new NrlkException("L'ID du ticket est obligatoire.");
        }

        const ticket = new NrlkTicket(window.entreprise, window.token);

        const requestBody = {
            "intervention": intervention
        };

        const data = await ticket.addInterventionsTicket(idTicket, requestBody);
        if (!data.result) {
            throw new NrlkException("Erreur lors de l'ajout de l'intervention : " + data.message);
        }
        return data.data;
    } catch (error) {
        throw new NrlkException(error);
    } finally {
        isAddingIntervention = false;
    }
}

/**
 * Ajouter fichier(s) dans un ticket (API V2)
 *
 * Changements V2 :
 * - Champ FormData : "fichier" (était "files[]")
 */
export async function addFichiersTicket(idTicket, fichiers) {
    try {
        if (!idTicket || isNaN(idTicket)) {
            throw new NrlkException("L'ID du ticket est obligatoire.");
        }
        if (!fichiers || fichiers.length === 0) {
            throw new NrlkException("Un ou plusieurs fichiers sont obligatoires.");
        }

        let fichiersUpload = new FormData();

        for (const fichier of fichiers) {
            if (!fichier.name) throw new NrlkException("Le nom du fichier est obligatoire.");
            if (!fichier.size || isNaN(fichier.size)) throw new NrlkException("La taille du fichier est obligatoire.");
            if (!fichier.type) throw new NrlkException("Le type du fichier est obligatoire.");

            if (fichier.size > 10000000) {
                throw new NrlkException("Le fichier est trop lourd, maximum 10Mo.");
            }
            if (!["application/pdf", "image/jpeg", "image/png"].includes(fichier.type)) {
                throw new NrlkException("Le format du fichier n'est pas autorisé (pdf, jpeg, png).");
            }

            fichiersUpload.append("fichier", fichier);
        }

        const ticket = new NrlkTicket(window.entreprise, window.token);
        const data = await ticket.addFichiersTicket(idTicket, fichiersUpload);

        if (!data.result) {
            throw new NrlkException("Erreur lors de l'ajout du fichier : " + data.message);
        }
        return data.data;
    } catch (error) {
        throw new NrlkException(error.message || error);
    }
}
